import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSite } from "../context/SiteContext";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { Switch } from "./ui/switch";
import { Brand } from "./Header";

const LINK = "text-sm text-[var(--sand)] hover:text-[var(--gold-soft)] transition-colors w-fit";

const LegalLink = ({ href, children, testId }) =>
  href.startsWith("/") ? (
    <Link to={href} data-testid={testId} className={LINK}>{children}</Link>
  ) : (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testId} className={LINK}>{children}</a>
  );

const Column = ({ title, children, testId }) => (
  <div className="flex flex-col items-start gap-3.5" data-testid={testId}>
    <p className="text-[11px] tracking-[0.2em] uppercase text-[var(--gold-soft)] mb-1">{title}</p>
    {children}
  </div>
);

export const Footer = () => {
  const { t } = useTranslation();
  const { settings, reducedMotion, setReducedMotion } = useSite();
  const legal = settings.legal_links || {};
  return (
    <footer className="relative bg-[var(--dusk)] text-[var(--cream)] grain" data-testid="site-footer">
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-20 grid gap-12 lg:gap-16 md:grid-cols-12 items-start">
        <div className="md:col-span-4 space-y-5">
          <Brand dark large />
          <p className="text-[var(--sand)]/80 max-w-xs leading-relaxed">{t("footer.tagline")}</p>
          <p className="text-[11px] tracking-[0.2em] uppercase text-[var(--gold-soft)] max-w-xs leading-relaxed">{t("footer.free")}</p>
        </div>
        <div className="md:col-span-8 grid gap-10 sm:grid-cols-3 items-start md:pt-6">
          <Column title={t("footer.col_legal")} testId="footer-col-legal">
            <LegalLink href={legal.privacy || "/privacy"} testId="footer-privacy">{t("footer.privacy")}</LegalLink>
            <LegalLink href={legal.cookies || "/cookies"} testId="footer-cookies">{t("footer.cookies")}</LegalLink>
            <LegalLink href={legal.terms || "/terms"} testId="footer-terms">{t("footer.terms")}</LegalLink>
          </Column>
          <Column title={t("footer.col_help")} testId="footer-col-help">
            <Link to="/contact" data-testid="footer-contact" className={LINK}>{t("footer.contact")}</Link>
            <Link to="/recover" data-testid="footer-recover" className={LINK}>{t("footer.recover")}</Link>
            <Link to="/memorials" data-testid="footer-list" className={LINK}>{t("footer.list_mode")}</Link>
          </Column>
          <Column title={t("footer.col_prefs")} testId="footer-col-prefs">
            <LanguageSwitcher variant="dark" testId="footer-language-switcher" />
            <label className="flex items-center gap-3 text-sm text-[var(--sand)] cursor-pointer">
              <Switch checked={reducedMotion} onCheckedChange={setReducedMotion} data-testid="reduce-motion-switch" />
              {t("nav.reduce_motion")}
            </label>
            {settings.contact_email && (
              <a href={`mailto:${settings.contact_email}`} className={`${LINK} break-all`} data-testid="footer-email">{settings.contact_email}</a>
            )}
          </Column>
        </div>
      </div>
      <div className="relative border-t border-white/10 py-6 text-center text-xs text-[var(--sand)]/60">
        © {new Date().getFullYear()} {t("brand.name")}
      </div>
    </footer>
  );
};
